import type { TrackPoint, VehicleTrack } from './types'

/** One decimated GPS fix from Postgres trip_path. */
export interface TripPathPoint {
  seq: number
  ts: number
  lat: number
  lon: number
  speed_kph: number | null
  heading_deg: number | null
  soc_pct: number | null
}

export interface TripPathPayload {
  kind: 'trip' | 'round_trip'
  trip_id?: string | null
  round_trip_id?: string | null
  vehicle_id: string
  points: TripPathPoint[]
  point_count: number
}

/** Keep fixes up to scrubber time for progressive path draw. */
export function slicePathByTime(points: TripPathPoint[], at: number): TripPathPoint[] {
  if (!points.length) return []
  return points.filter((p) => p.ts <= at)
}

export interface PathPosition {
  lat: number
  lon: number
  ts: number
  speed_kph: number | null
  heading_deg: number | null
}

/** Bearing degrees from segment a→b (0 = north, clockwise). */
export function bearingDeg(a: Pick<TripPathPoint, 'lat' | 'lon'>, b: Pick<TripPathPoint, 'lat' | 'lon'>): number {
  const dLon = ((b.lon - a.lon) * Math.PI) / 180
  const lat1 = (a.lat * Math.PI) / 180
  const lat2 = (b.lat * Math.PI) / 180
  const y = Math.sin(dLon) * Math.cos(lat2)
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

/** Below this, two fixes are the same spot (GPS jitter while parked): keep the stored
 *  heading rather than spin the icon toward the scatter. */
export const MIN_TRAVEL_FOR_BEARING_M = 25

export function distanceM(a: Pick<TripPathPoint, 'lat' | 'lon'>, b: Pick<TripPathPoint, 'lat' | 'lon'>): number {
  const r = Math.PI / 180
  const dLat = (b.lat - a.lat) * r
  const dLon = (b.lon - a.lon) * r
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLon / 2) ** 2
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Interpolate GPS position and heading at scrubber time. */
export function positionAtTime(points: TripPathPoint[], at: number): PathPosition | null {
  if (!points.length) return null
  let i = -1
  for (let j = 0; j < points.length; j++) {
    if (points[j]!.ts <= at) i = j
    else break
  }
  if (i < 0) return null
  const a = points[i]!
  if (i >= points.length - 1) {
    // Past the last fix: keep facing the way it arrived, if it was moving.
    const prev = points[i - 1]
    const arrived = prev && distanceM(prev, a) >= MIN_TRAVEL_FOR_BEARING_M ? bearingDeg(prev, a) : null
    return {
      lat: a.lat, lon: a.lon, ts: a.ts,
      speed_kph: a.speed_kph, heading_deg: arrived ?? a.heading_deg,
    }
  }
  const b = points[i + 1]!
  const span = b.ts - a.ts
  const t = span > 0 ? Math.max(0, (at - a.ts) / span) : 0
  let heading = b.heading_deg ?? a.heading_deg
  if (distanceM(a, b) >= MIN_TRAVEL_FOR_BEARING_M) {
    // The truck actually travelled between these two fixes: face along the road it took.
    // The device compass (heading_deg) is an instantaneous reading -- noisy at low speed
    // and on bends -- and the stored path is decimated, so the segment between two kept
    // fixes is the direction of travel the replay is drawing.
    heading = bearingDeg(a, b)
  } else if (a.heading_deg != null && b.heading_deg != null) {
    let diff = b.heading_deg - a.heading_deg
    if (diff > 180) diff -= 360
    if (diff < -180) diff += 360
    heading = (a.heading_deg + diff * t + 360) % 360
  } else if (heading == null) {
    heading = bearingDeg(a, b)
  }
  const speed = a.speed_kph != null && b.speed_kph != null
    ? a.speed_kph + (b.speed_kph - a.speed_kph) * t
    : b.speed_kph ?? a.speed_kph
  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lon: a.lon + (b.lon - a.lon) * t,
    ts: at,
    speed_kph: speed,
    heading_deg: heading,
  }
}

export interface ReplayPathEvent {
  kind?: string
  ts?: number
  lat?: number | null
  lon?: number | null
}

/** Vehicle position for trip replay — GPS path first, geotag/halt fallback. */
export function replayVehiclePosition(
  events: readonly ReplayPathEvent[],
  path: TripPathPoint[] | null | undefined,
  at: number,
): PathPosition & { halted: boolean } | null {
  const past = events.filter((e) => (e.ts || 0) <= at && e.lat != null && e.lon != null)
  const cur = past[past.length - 1]
  const halted = cur?.kind === 'halt'
  const pathPos = path?.length ? positionAtTime(path, at) : null
  if (pathPos) {
    return {
      ...pathPos,
      speed_kph: halted ? 0 : pathPos.speed_kph,
      halted,
    }
  }
  if (!cur || cur.lat == null || cur.lon == null) {
    // No geotag yet either (a GPS-built round trip can have none): before the first GPS
    // fix the truck is where that first fix says -- not nowhere, which would leave the
    // map without the replayed truck.
    const first = path?.[0]
    if (first && at < first.ts) {
      return { lat: first.lat, lon: first.lon, ts: first.ts, speed_kph: first.speed_kph, heading_deg: first.heading_deg, halted: false }
    }
    return null
  }
  return {
    lat: cur.lat,
    lon: cur.lon,
    ts: at,
    speed_kph: halted ? 0 : 40,
    heading_deg: null,
    halted,
  }
}

/** Convert trip_path rows into the map track layer format. */
export function pathToTrack(
  vehicleId: string,
  points: TripPathPoint[],
  at?: number,
  opts?: { showEnds?: boolean },
): VehicleTrack | null {
  const slice = at == null ? points : slicePathByTime(points, at)
  if (slice.length < 2) return null
  const track: TrackPoint[] = slice.map((p) => ({
    lat: p.lat,
    lon: p.lon,
    ts: p.ts,
    speed_kph: p.speed_kph,
    soc_pct: p.soc_pct,
  }))
  return {
    vehicle_id: vehicleId,
    from: track[0]!.ts,
    to: track[track.length - 1]!.ts,
    track,
    fixes: track.length,
    fence_events: [],
    alerts: [],
    show_ends: opts?.showEnds,
  }
}
